import { TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';

import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { UpdateBooleanServerSettingNames } from '../../../../ServerApi/requests/serverSettings/UpdateBooleanServerSettingRequest';
import { ServerSettingsInstance } from '../../../../services/ServerSettings';
import { ServerSettingsUnauthenticated } from '../../../../services/ServerSettingsUnauthenticated';
import { useSelector } from '../../../utils';
import { ServerSettingsProps } from '../../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForServerAdminProps } from '../../site-settings/ReactSiteSettingsForServerAdmin';

export const SERVER_GUEST_ACCESS_FIELD = 'settings.guestEnabled';
export const SITE_GUEST_ACCESS_FIELD = 'settings.guestAccessEnabled';

export type SiteGuestAccessSectionProps = SiteSettingsForServerAdminProps<'guestAccessEnabled'>;

export type ServerGuestAccessSectionProps = ServerSettingsProps<'guestEnabled'>;
export interface InternalGuestAccessSectionProps {
  disabled?: boolean;
  title: React.ReactNode;
  description: string;
  sectionTestId: string;
  children?: React.ReactNode;
}

const InternalGuestAccessSection: React.FunctionComponent<InternalGuestAccessSectionProps> = function InternalGuestAccessSection(props: InternalGuestAccessSectionProps) {
  return <SettingsSection title={props.title}
                          description={props.description}
                          testId={props.sectionTestId}
                          disabled={props.disabled}>
    <span>{props.children}</span>
  </SettingsSection>;
};

export function ServerGuestAccessSection(props: ServerGuestAccessSectionProps) {
  const isMultiSite = useSelector<boolean>(SessionUserSelectors.isSessionMultiSite); // check if same for Site version
  const description = isMultiSite ? Messages.guestAccessDetailsForServer() : Messages.guestAccessDetailsForSite();
  return <InternalGuestAccessSection disabled={props.settings.guestEnabled === undefined}
                                     title={Messages.guestAccess()}
                                     description={description}
                                     sectionTestId={'server-guest-access-setting-section'}>
    <FormikCheckbox name={SERVER_GUEST_ACCESS_FIELD}
                    label={Messages.guestControlLabelForServer()}
                    testId={'guest-enabled'}
                    disabled={props.settings.guestEnabled === undefined}>
    </FormikCheckbox>
  </InternalGuestAccessSection>;
}

export function SiteGuestAccessSection(props: SiteGuestAccessSectionProps) {
  const guestAccessEnabledOnServer= ServerSettingsUnauthenticated.isGuestEnabled();
  const isMultiSite = useSelector<boolean>(SessionUserSelectors.isSessionMultiSite);
  const showGuestAccessControl = isMultiSite || !props.isDefaultSite;
  const spacer = style({
    paddingBottom: TabStyles.Spacing.MediumSpace
  });

  return (showGuestAccessControl)
  ? (
    <InternalGuestAccessSection title={Messages.guestAccess()}
                                description={Messages.guestAccessDetailsForSite()}
                                sectionTestId={'site-guest-access-setting-section'}>
      {!guestAccessEnabledOnServer &&
        <div className={spacer} id='guest-access-control-message-id'>
          {Messages.guestAccessDisabledOnSite()}
        </div>}
      <FormikCheckbox name={SITE_GUEST_ACCESS_FIELD}
                      label={Messages.guestControlLabelForSite()}
                      testId={'single-site-guest-enabled'}
                      disabled={!guestAccessEnabledOnServer}>
      </FormikCheckbox>
    </InternalGuestAccessSection>
  )
  : null;
}

/* for registering the component in settings registry */
export const ServerGuestAccessSectionSpecs = componentRegistryEntry({
  name: 'ServerGuestAccessSection',
  component: ServerGuestAccessSection,
  onSubmit: (settings) => {
    if(settings.guestEnabled !== undefined) {
      return ServerSettingsInstance.updateBoolean(UpdateBooleanServerSettingNames.guestEnabled, settings.guestEnabled);
    }
    return null;
  }
});

export const SiteGuestAccessSectionSpecs = componentRegistryEntry({
  name: 'SiteGuestAccessSection',
  component: SiteGuestAccessSection,
});

